import JSZip from 'jszip';
import {
    DicomBase,
    GenerationModelDetails,
    ObjectStatus,
} from '~/domain/dicom/dicomBase';
import { DicomProcessor } from '~/domain/services/dicomProcessor';
import { handleError } from '~/utils/errors';
import { decompressGZip } from './gzip';

export type SkipReason = 'unrecognized-extension' | 'unimportable-dicom';

export type SkipEntry = {
    reason: SkipReason;
    fileName: string;
};

export type SkipReport = {
    examined: number;
    skips: SkipEntry[];
};

export function createSkipReport(): SkipReport {
    return { examined: 0, skips: [] };
}

/**
 * Splits a file list into DICOM files and non-DICOM JSON files.
 *
 * The backend worker may return JSON files (optimization objectives, metadata,
 * etc.) alongside DICOM files. Passing JSON files to the DICOM anonymizer /
 * re-identifier causes it to throw and return null, silently dropping all
 * results. This function separates them at the source so only genuine DICOM
 * files reach the DICOM pipeline.
 */
export function splitDicomAndJsonFiles(files: File[]): {
    dicomFiles: File[];
    jsonFiles: File[];
} {
    const dicomFiles: File[] = [];
    const jsonFiles: File[] = [];
    for (const file of files) {
        if (file.name.toLowerCase().endsWith('.json')) {
            jsonFiles.push(file);
        } else {
            dicomFiles.push(file);
        }
    }
    return { dicomFiles, jsonFiles };
}

export function requestFileInput(
    onFileLoaded: (data: ArrayBuffer) => unknown,
    multiple: boolean
) {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = multiple;
    input.onchange = function () {
        const files = input.files;
        if (files) {
            for (let i = 0; i < files.length; ++i) {
                const file = files.item(i);
                if (file) {
                    const reader = new FileReader();
                    reader.onload = () => {
                        if (reader.result) {
                            onFileLoaded(reader.result as ArrayBuffer);
                        }
                    };
                    reader.onerror = () => {
                        throw new Error(`FileHandler.requestFileInput faced an error reading file ${file.name}:\
                        ${reader.error}`);
                    };
                    reader.readAsArrayBuffer(file);
                }
            }
        }
    };
    input.click();
    input.remove();
}

/**
 * Function that will get a complete filename as a parameter and divide it into fileName and extension.\
 * The filename is divided by the last dot. And the dot won't be included in either the fileName nor the extension.\
 * The extension is lowercased so it can be matched case-insensitively, while the
 * fileName keeps its original casing so it can be shown back to the user as-is.\
 * input of "dicom.dcm.gz" will return {fileName: "dicom.dcm", fileExtension: "gz"}\
 * input of "dicom.dcm" will return {fileName: "dicom", fileExtension: "dcm"}
 * @param completeName The filename with it's file extension. Format [filename].[extension]
 * @returns Type with both the fileName and fileExtension.\
 * If the dot is missing or it is the first or last character in the filename, fileExtension is undefined.
 */
function extractFilenameAndExtension(completeName: string): {
    fileName: string;
    fileExtension?: string;
} {
    const lastDot = completeName.lastIndexOf('.');
    if (lastDot > 0 && lastDot < completeName.length - 1) {
        return {
            fileName: completeName.substring(0, lastDot),
            fileExtension: completeName.substring(lastDot + 1).toLowerCase(),
        };
    } else {
        return { fileName: completeName };
    }
}

/**
 * Function to process a {@link File}.\
 * It's extension is extracted to know what kind of data the {@link File} should contain.
 * File's contents are converted into {@link ArrayBuffer} asynchronously.\
 * Once the conversion has been completed, the contents are given to {@link processFileContent} function for processing.
 *
 * If the file extension is .zip it first decompresses it and then process files inside.
 *
 * @param file {@link File} the contents of which are to be processed.
 */
export async function processFile(
    file: File,
    dicomProcessor: DicomProcessor,
    objectStatus?: ObjectStatus,
    model?: GenerationModelDetails,
    report?: SkipReport
): Promise<DicomBase | null> {
    const { fileName, fileExtension } = extractFilenameAndExtension(file.name);
    if (fileExtension === 'zip') {
        const zip = new JSZip();
        const unzipped = await zip.loadAsync(file);
        const files = Object.keys(unzipped.files);
        await Promise.all(
            files.map(async (innerName) => {
                const entry = unzipped.files[innerName];
                if (entry.dir) {
                    return null;
                }
                const blob = await entry.async('blob');
                return await processFile(
                    new File([blob], innerName),
                    dicomProcessor,
                    objectStatus,
                    model,
                    report
                );
            })
        );
        return null;
    } else {
        try {
            const fileArrayBuffer = await file.arrayBuffer();
            const dicomData = processFileContent(
                dicomProcessor,
                fileArrayBuffer,
                fileName,
                fileExtension,
                report
            );
            if (dicomData && objectStatus) {
                dicomData.objectStatus = objectStatus;
            }
            if (dicomData && model) {
                dicomData.generationModel = model;
            }
            return dicomData;
        } catch (error) {
            handleError(
                error,
                `Error processing file '${file.name}': ${error}`,
                'processFileError'
            );
            return null;
        }
    }
}

/**
 * Function that will process {@link ArrayBuffer} contents of different types of {@link File Files}.\
 * {@link fileExtension} is used to determine the type of data. And the correct processing functionality.
 * @param arrayBuffer Contents of a {@link File} that is to be processed.
 * @param fileName The filename without it's last extension.
 * @param fileExtension Filename's last extension.
 * @returns True if the file contents could successfully be processed, false otherwise.
 */
export function processFileContent(
    dicomProcessor: DicomProcessor,
    arrayBuffer: ArrayBuffer,
    fileName: string,
    fileExtension?: string,
    report?: SkipReport
): DicomBase | null {
    // Count terminal files exactly once. The gz branch recurses and the
    // inner call will count the unwrapped file, so we skip incrementing
    // here when the extension is gz.
    if (fileExtension !== 'gz' && report) {
        report.examined += 1;
    }
    switch (fileExtension) {
        case 'gz': {
            const nameAndExtension = extractFilenameAndExtension(fileName);
            return processFileContent(
                dicomProcessor,
                decompressGZip(arrayBuffer),
                nameAndExtension.fileName,
                nameAndExtension.fileExtension,
                report
            );
        }
        case 'dcm': {
            try {
                const dicomData = dicomProcessor.processDicomData(arrayBuffer);
                if (!dicomData) {
                    // Log only the reason, not the filename: names can be PHI
                    // and are surfaced to the user via the SkipReport toast.
                    console.warn(
                        'Skipping DICOM file (unsupported or unimportable)'
                    );
                    report?.skips.push({
                        reason: 'unimportable-dicom',
                        fileName: `${fileName}.${fileExtension}`,
                    });
                }
                return dicomData;
            } catch {
                // Don't log the thrown error or filename: both can carry PHI
                // (buffer/identifying bits). The name reaches the user via the
                // SkipReport toast instead.
                console.warn('Skipping file that could not be parsed as DICOM');
                report?.skips.push({
                    reason: 'unimportable-dicom',
                    fileName: `${fileName}.${fileExtension}`,
                });
                return null;
            }
        }
        default: {
            // Files without an extension (e.g. ".DS_Store") have an
            // undefined fileExtension; format the name conditionally so the
            // SkipReport entry shown to the user matches.
            const displayName = fileExtension
                ? `${fileName}.${fileExtension}`
                : fileName;
            // Log only the extension, not the filename: names can be PHI and
            // are surfaced to the user via the SkipReport toast.
            console.warn(
                `Skipping file with unrecognized extension:` +
                    ` ${fileExtension ? `.${fileExtension}` : '(none)'}`
            );
            report?.skips.push({
                reason: 'unrecognized-extension',
                fileName: displayName,
            });
            return null;
        }
    }
}
